import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useTicker } from 'tradera/hooks/use-ticker';
import { syncServerTime } from 'tradera/utils/server-date';
import { useTranslator } from 'tradera/lang/translate';
import { useDayJs } from 'tradera/hooks/use-day-js';
import { useIsMounted } from 'tradera/hooks/use-is-mounted';
import {
    DAY_IN_SECONDS,
    HOUR_IN_SECONDS,
    MINUTE_IN_MILLISECONDS,
    SECOND_IN_MILLISECONDS,
} from 'tradera/constants/time';

const COUNTDOWN_DEFAULT_WARNING_LIMIT = 12 * 60 * 60;

const getCountdownTickInterval = (endDate) => {
    const now = new Date().getTime();
    const secondsLeft = (endDate.getTime() - now) / 1000;
    if (secondsLeft > DAY_IN_SECONDS) {
        return MINUTE_IN_MILLISECONDS * 5;
    } else if (secondsLeft > HOUR_IN_SECONDS) {
        return MINUTE_IN_MILLISECONDS;
    } else {
        return SECOND_IN_MILLISECONDS;
    }
};

/**
 *
 * @return {{timeLeftText: string, isCountdownEnabled: boolean, isCountdownDone: boolean, isTimeLeftBelowWarningLimit: boolean}}
 * isCountdownEnabled - True if client time is synched with server time.
 * isCountdownDone - True if there is no time left after counting down.
 * timeLeftText - The time left, formatted by the given formatter function.
 * isTimeLeftBelowWarningLimit - True if time is below the given warningLimit.
 */
export const useCountdown = (endDateStr, formatTimeLeft) => {
    const { t } = useTranslator();
    const warningLimit = COUNTDOWN_DEFAULT_WARNING_LIMIT;
    const [isCountdownDone, setIsCountdownDone] = useState(false);
    const [timeLeftText, setTimeLeftText] = useState('');
    const [isCountdownEnabled, setIsCountdownEnabled] = useState(false);
    const [isTimeLeftBelowWarningLimit, setIsTimeLeftBelowWarningLimit] =
        useState(false);
    const previousAdjustment = useRef(0);
    const [synchronizedEndDateStr, setSynchronizedDateStr] =
        useState(endDateStr);
    const endDate = useMemo(() => new Date(endDateStr), [endDateStr]);
    const isMounted = useIsMounted(true);
    const dayjs = useDayJs();

    const resetState = useCallback(() => {
        setIsCountdownEnabled(false);
        setIsCountdownDone(false);
        setTimeLeftText('');
        setIsTimeLeftBelowWarningLimit(false);
    }, []);

    useEffect(() => {
        setSynchronizedDateStr(endDateStr);
        resetState();
    }, [endDateStr, resetState]);

    const updateTimeLeft = useCallback(
        async (isInitialCall = false) => {
            const serverDate = await syncServerTime();
            if (!isMounted) {
                // Can't perform a React state update on an unmounted component.
                // This is a no-op, but it indicates a memory leak in your application.
                return;
            }
            if (!serverDate) {
                // In case server date is lost we need to
                // ensure everything looks OK.
                resetState();
                return;
            }
            setIsCountdownEnabled(true);

            const secondsLeftWithFractions = (endDate - serverDate) / 1000;
            const previousAdjustmentValue = isNaN(previousAdjustment.current)
                ? 0
                : previousAdjustment.current;
            const adjustedSecondsLeftWithFractions =
                secondsLeftWithFractions - previousAdjustmentValue;
            let secondsLeft = Math.round(adjustedSecondsLeftWithFractions);

            // The amount we adjusted the time at this tick.
            // We use this at the next tick to make adjustedSecondsLeftWithFractions
            // as close to whole seconds as possible.
            // In other words adjustedSecondsLeftWithFractions - secondsLeft should be as close to zero
            // as possible.
            let adjustment = secondsLeftWithFractions - secondsLeft;

            // Don't let the adjustment to drift over a second up or down.
            if (adjustment >= 1 || adjustment <= -1) {
                // Reset the adjustment
                secondsLeft = Math.round(secondsLeftWithFractions);
                adjustment = secondsLeftWithFractions - secondsLeft;
            }

            // The first time here we are not in a tick so
            // ignore adjustment.
            if (!isInitialCall) {
                previousAdjustment.current = adjustment;
            }

            const formattedTimeLeft =
                secondsLeft > 0
                    ? formatTimeLeft(secondsLeft, t, endDate, serverDate, dayjs)
                    : t('vip_ended');

            setTimeLeftText(formattedTimeLeft);
            setIsTimeLeftBelowWarningLimit(secondsLeft <= warningLimit);

            if (secondsLeft <= 0) {
                setIsCountdownDone(true);
            }
        },
        [
            endDate,
            formatTimeLeft,
            t,
            dayjs,
            warningLimit,
            resetState,
            isMounted,
        ],
    );

    useEffect(() => {
        updateTimeLeft(true);
    }, [updateTimeLeft]);

    useTicker(
        updateTimeLeft,
        isCountdownDone || !isMounted ? 0 : getCountdownTickInterval(endDate),
    );

    // Ensure countdown is not for previous date.
    if (endDateStr !== synchronizedEndDateStr) {
        return {
            timeLeftText: '',
            isCountdownDone: false,
            isCountdownEnabled: false,
            isTimeLeftBelowWarningLimit: false,
        };
    }

    return {
        timeLeftText,
        isCountdownDone,
        isCountdownEnabled,
        isTimeLeftBelowWarningLimit,
    };
};
